import { withFormProps } from '@kizi/comparator-components';
import { useField, usePage, useWizardService } from '@kizi/comparator-core';
import { Div, Label, Span } from '@kizi/core-components';
import { Radio } from '@kizi/shared-components';
import React, { useEffect } from 'react';
import { CostInfo, Option, transformCost } from '../../utils/extraOptions';
import { SummaryCostInfo } from '../wrappers/ProductSummaryWrapper';

interface ExtraOptionsRadioProps {
  _label: string;
  _options: Option[];
  name: string;
}

const LabelWithTooltip = withFormProps(Label);
const DivWithFormProps = withFormProps(Div);
const SpanWithTooltip = withFormProps(Span);

export const ExtraOptionsRadio: React.FC<ExtraOptionsRadioProps> = ({
  _label,
  _options,
  name,
  ...props
}) => {
  const field = useField(name);
  const wizard = useWizardService();
  const { state, setState } = usePage();

  const setProductSummaryOption = (
    value: string | undefined,
    discountDurationInMonths?: number,
    discountTariff?: number,
  ) => {
    if (!state || !setState) return;

    const costType = _options.find(
      (option) => option?.cost?.monthly.quantity > 0,
    )
      ? 'monthly'
      : 'onceOff';

    const data = (state[costType] || []) as SummaryCostInfo[];
    const index = data.findIndex(
      (extraOption) => extraOption.fieldName === name,
    );

    const cost = _options.find((option) => option.value === value)
      ?.cost as CostInfo;

    if (index > -1) {
      if (cost) {
        data[index] = {
          fieldName: name,
          name: cost.name,
          rates: cost[costType]?.items || [],
        };
      } else {
        data.splice(index, 1);
      }
    } else if (cost) {
      data.push({
        fieldName: name,
        name: cost.name,
        rates: cost[costType]?.items || [],
      });
    }

    // Hello reader 😅 the year is 2024...and THIS... is Mary's last commit!
    // Hopefully you won't need to change the code below... but let's be real... this is triple-play
    // Mary out
    setState({
      ...state,
      [costType]: data,
      applyDiscount: _label.includes('contractduur')
        ? discountDurationInMonths === 0
          ? false
          : true
        : _label.includes('aanbieding')
          ? discountDurationInMonths === 0
            ? false
            : true
          : state.applyDiscount,
      discountDurationInMonths: _label.includes('contractduur')
        ? discountDurationInMonths
        : state.discountDurationInMonths,
      discountTariff: _label.includes('contractduur')
        ? discountTariff
        : state.discountTariff,
    });
  };

  useEffect(() => {
    return () => {
      if (!wizard?.getSnapshot()?.event?.type?.includes('done')) {
        field?.set({ value: undefined });
      }
    };
  }, []);

  return (
    <DivWithFormProps
      mb={2}
      _errorProps={{ variant: 'error' }}
      _successProps={{ variant: 'success' }}
      {...props}
      _containerProps={{ pb: 0 }}
    >
      <LabelWithTooltip
        _containerProps={{
          flexDirection: 'row-reverse',
          justifyContent: 'flex-end',
          mt: 2,
        }}
        _label={_label}
      />

      {_options.map(
        (
          {
            label,
            value,
            cost,
            description,
            discountDurationInMonths,
            discountTariff,
          },
          index,
        ) => (
          <Div variant="extraOptionsCheckbox" key={label}>
            <Radio
              label={label.replace(/&euro;/gi, '€')}
              value={value}
              checked={field?.value === value}
              key={index}
              variant="main"
              onChange={() => {
                field?.set({ value });
                setProductSummaryOption(
                  value,
                  discountDurationInMonths,
                  discountTariff,
                );
              }}
              containerProps={{
                flexDirection: 'row-reverse',
                justifyContent: 'flex-end',
              }}
            />
            {(cost?.onceOff || cost?.monthly) && (
              <SpanWithTooltip
                _tooltip={description || ''}
                _containerProps={{ flexDirection: 'row-reverse' }}
              >
                {transformCost(cost)}
              </SpanWithTooltip>
            )}
          </Div>
        ),
      )}
    </DivWithFormProps>
  );
};
